import React, { useMemo, useState } from "react";

import { FloatingPortal } from "@floating-ui/react";
import {
  faCheckCircle,
  faPlay,
  faTimesCircle,
} from "@fortawesome/pro-regular-svg-icons";
import { isAxiosError } from "axios";

import { useMockTemplate } from "src/api/connectApi/queries";
import {
  MockTemplateCaseResult,
  MockTemplateResponse,
} from "src/api/connectApi/types";
import { Button } from "src/base-components/Button";
import { CodeEditor } from "src/base-components/CodeInput/CodeEditor";
import { CopyTextIcon } from "src/base-components/CopyTextIcon";
import { FloatingWindowInternal } from "src/base-components/FloatingWindow/FloatingWindow";
import { Input } from "src/base-components/Input";
import { Pill } from "src/base-components/Pill";
import { Accordion } from "src/design-system/Accordion";
import { Callout } from "src/design-system/Callout";
import { useAuthoringContext } from "src/router/routerContextHooks";

const CodeOutput: React.FC<{ value: string; language?: "json" }> = ({
  value,
  language = "json",
}) => (
  <div className="relative overflow-auto rounded-lg border border-gray-200">
    <div className="absolute top-2 right-2 z-10">
      <CopyTextIcon size="sm" tooltip="Copy to clipboard" value={value} />
    </div>
    <CodeEditor readOnly language={language} value={value} />
  </div>
);

const MockResultItem: React.FC<{
  result: MockTemplateCaseResult;
}> = ({ result }) => {
  return (
    <Accordion.Item
      dataLoc={`mock-result-case-${result.case_number}`}
      rightSlot={
        <Pill size="sm" variant={result.success ? "green" : "red"}>
          <Pill.Icon
            icon={result.success ? faCheckCircle : faTimesCircle}
            size="sm"
          />
          <Pill.Text>{result.success ? "Passed" : "Failed"}</Pill.Text>
        </Pill>
      }
      label={`Case ${result.case_number}`}
      value={`case-${result.case_number}`}
    >
      {result.success ? (
        <div>
          <div className="mb-1 font-inter-medium-11px text-gray-600">
            Rendered Output
          </div>
          <CodeOutput
            value={
              typeof result.rendered_template === "string"
                ? result.rendered_template
                : JSON.stringify(result.rendered_template, null, 2)
            }
          />
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          <Callout variant="warning">
            {result.error?.message}
            {result.error?.details && (
              <div className="mt-1 text-gray-700">{result.error.details}</div>
            )}
          </Callout>
          {result.error?.suggestion && (
            <div className="font-inter-medium-11px text-gray-600">
              {result.error.suggestion}
            </div>
          )}
          {result.rendered_template_raw && (
            <div>
              <div className="mb-1 font-inter-medium-11px text-gray-600">
                Raw Output (for debugging)
              </div>
              <CodeOutput value={result.rendered_template_raw} />
            </div>
          )}
        </div>
      )}
      <div className="mt-2">
        <details className="text-gray-500">
          <summary className="cursor-pointer font-inter-medium-11px">
            Mock Data Used
          </summary>
          <CodeOutput value={JSON.stringify(result.mock_data, null, 2)} />
        </details>
      </div>
    </Accordion.Item>
  );
};

type ValidationControlsProps = {
  numCases: number;
  seed: number | undefined;
  isPending: boolean;
  disabled: boolean;
  onNumCasesChange: (val: number) => void;
  onSeedChange: (val: number | undefined) => void;
  onValidate: () => void;
};

const ValidationControls: React.FC<ValidationControlsProps> = ({
  numCases,
  seed,
  isPending,
  disabled,
  onNumCasesChange,
  onSeedChange,
  onValidate,
}) => (
  <div className="flex items-center gap-2">
    <Button
      dataLoc="validate-code-button"
      disabled={isPending || disabled}
      iconLeft={faPlay}
      loading={isPending}
      size="sm"
      variant="primary"
      onClick={onValidate}
    >
      Validate
    </Button>
    <div className="flex items-center gap-1">
      <span className="font-inter-medium-12px text-gray-600">Cases</span>
      <Input
        data-loc="validate-num-cases"
        max={30}
        min={1}
        type="number"
        value={numCases}
        onChange={(e) => {
          const val = Math.max(1, Math.min(30, Number(e.target.value) || 1));
          onNumCasesChange(val);
        }}
      />
    </div>
    <div className="flex items-center gap-1">
      <span className="font-inter-medium-12px text-gray-600">Seed</span>
      <Input
        data-loc="validate-seed"
        min={0}
        placeholder="Random"
        type="number"
        value={seed ?? ""}
        onChange={(e) => {
          const raw = e.target.value;
          onSeedChange(raw === "" ? undefined : Number(raw));
        }}
      />
    </div>
  </div>
);

const ValidationResultsPanel: React.FC<{
  response: MockTemplateResponse;
  numCases: number;
  seed: number | undefined;
  isPending: boolean;
  disabled: boolean;
  onNumCasesChange: (val: number) => void;
  onSeedChange: (val: number | undefined) => void;
  onValidate: () => void;
}> = ({
  response,
  numCases,
  seed,
  isPending,
  disabled,
  onNumCasesChange,
  onSeedChange,
  onValidate,
}) => {
  const allPassed = response.successful_cases === response.total_cases;

  const defaultOpenCases = response.results
    .filter((result, idx) => !result.success || idx === 0)
    .map((result) => `case-${result.case_number}`);

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-2 border-gray-200 border-b px-3 py-2">
        <Pill size="sm" variant={allPassed ? "green" : "red"}>
          <Pill.Icon
            icon={allPassed ? faCheckCircle : faTimesCircle}
            size="sm"
          />
          <Pill.Text>
            {response.successful_cases}/{response.total_cases} passed
          </Pill.Text>
        </Pill>

        <div className="ml-auto">
          <ValidationControls
            disabled={disabled}
            isPending={isPending}
            numCases={numCases}
            seed={seed}
            onNumCasesChange={onNumCasesChange}
            onSeedChange={onSeedChange}
            onValidate={onValidate}
          />
        </div>

        <div className="self-end">
          <Pill size="lg" variant="indigo-light">
            <Pill.Text fontType="code-12">Seed: {response.seed}</Pill.Text>
            <CopyTextIcon
              variant="active"
              size="sm"
              tooltip="Copy seed to clipboard"
              value={response.seed.toString()}
            />
          </Pill>
        </div>
      </div>
      <div className="min-h-0 flex-1 overflow-auto p-3">
        <Accordion defaultValue={defaultOpenCases}>
          {response.results.map((result) => (
            <MockResultItem key={result.case_number} result={result} />
          ))}
        </Accordion>
      </div>
    </div>
  );
};

type Props = {
  template: string | undefined;
  inputSchema: Record<string, unknown> | undefined;
  bodyType: "json" | "xml" | "raw";
  connectionId?: string;
};

export const CodeValidationPanel: React.FC<Props> = ({
  template,
  inputSchema,
  bodyType,
  connectionId,
}) => {
  const [validationResults, setValidationResults] =
    useState<MockTemplateResponse | null>(null);
  const [numCases, setNumCases] = useState(5);
  const [seed, setSeed] = useState<number | undefined>(undefined);

  const { workspace } = useAuthoringContext();
  const baseUrl = workspace.base_url;

  const mockTemplateMutation = useMockTemplate(baseUrl);

  const isDisabled = !template || !inputSchema;

  const errorMessage = useMemo(() => {
    const error = mockTemplateMutation.error;
    if (!error) return undefined;
    if (isAxiosError(error) && error.response?.data?.detail) {
      const detail = error.response.data.detail;
      return typeof detail === "string" ? detail : JSON.stringify(detail);
    }
    return error.message;
  }, [mockTemplateMutation.error]);

  const handleValidate = () => {
    if (isDisabled) {
      return;
    }

    setValidationResults(null);

    mockTemplateMutation.mutate(
      {
        template: template!,
        input_schema: inputSchema!,
        body_type: bodyType,
        number_of_cases: numCases,
        seed,
        connection_id: connectionId,
      },
      {
        onSuccess: (data) => {
          setValidationResults(data);
        },
      },
    );
  };

  return (
    <div className="flex flex-col gap-2 py-2">
      {mockTemplateMutation.isError && (
        <Callout variant="error">
          {errorMessage || "Failed to validate code. Please try again."}
        </Callout>
      )}
      <ValidationControls
        disabled={isDisabled}
        isPending={mockTemplateMutation.isPending}
        numCases={numCases}
        seed={seed}
        onNumCasesChange={setNumCases}
        onSeedChange={setSeed}
        onValidate={handleValidate}
      />
      {validationResults && (
        <FloatingPortal>
          <FloatingWindowInternal
            isMaximized={true}
            isPinned={false}
            style={undefined}
            title="Validation Results"
            draggable
            maximizable
            resizeable
            onClose={() => setValidationResults(null)}
          >
            <ValidationResultsPanel
              disabled={isDisabled}
              isPending={mockTemplateMutation.isPending}
              numCases={numCases}
              response={validationResults}
              seed={seed}
              onNumCasesChange={setNumCases}
              onSeedChange={setSeed}
              onValidate={handleValidate}
            />
          </FloatingWindowInternal>
        </FloatingPortal>
      )}
    </div>
  );
};
